import React, { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Dashboard from './pages/Dashboard'
import Coda from './pages/Coda'
import Landing from './pages/Landing'
import Sidebar from './components/Sidebar'
import useIsMobile from './hooks/useIsMobile'

// Caricata solo se l'utente è admin (vedi ADMIN_EMAILS sotto): il lazy
// import evita di spedire nel bundle i dati mock di Analytics a chi non
// dovrebbe vederli.
const Analytics = lazy(() => import('./pages/Analytics'))

// Email autorizzate a vedere /analytics. Attenzione: questo è solo un
// controllo lato client (nasconde nav + rotta), non una vera protezione —
// Analytics oggi mostra solo dati mock hardcoded, quindi va bene così, ma
// quando userà un endpoint reale servirà anche un controllo lato server.
const ADMIN_EMAILS = [
  'luca.codeluppi@jenia.ai',
  'giacomo.levrini@jenia.ai',
  'michele.rinaldi@jenia.ai',
]

// /auth/me è gestito fuori da questa repo (SSO Microsoft, vedi commit
// e542005) e al momento non è confermato quale campo contenga l'email
// dell'utente loggato: proviamo i nomi più comuni (Microsoft Graph/Azure AD
// usano spesso "mail" o "userPrincipalName"). Se in produzione nessuno
// combacia, isAdmin resta false — fail-safe: si nasconde Analytics invece
// di rischiare di mostrarla a chi non dovrebbe vederla. In quel caso,
// controllare la risposta reale di /auth/me (tab Network del browser) e
// aggiungere il campo giusto qui sotto.
function extractEmail(payload) {
  if (!payload || typeof payload !== 'object') return null
  const candidates = [
    payload.email,
    payload.mail,
    payload.userPrincipalName,
    payload.upn,
    payload.preferred_username,
    payload.user?.email,
    payload.user?.mail,
  ]
  const found = candidates.find((v) => typeof v === 'string' && v.includes('@'))
  return found ? found.toLowerCase() : null
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const isMobile = useIsMobile(720)

  useEffect(() => {
    fetch('/auth/me', { credentials: 'same-origin' })
      .then(async (res) => {
        setIsAuthenticated(res.ok)
        if (!res.ok) return
        try {
          const data = await res.json()
          const email = extractEmail(data)
          if (email) {
            setIsAdmin(ADMIN_EMAILS.includes(email))
          } else if (import.meta.env.DEV) {
            console.warn(
              '[auth] /auth/me non contiene un campo email riconosciuto: Analytics resta nascosta. Payload ricevuto:',
              data
            )
          }
        } catch {
          // In sviluppo locale (`npm run dev`) non esiste un vero backend di
          // auth: /auth/me non è servito da nessuno, quindi Vite fa fallback
          // e restituisce index.html con status 200 — res.ok è true (per
          // questo su localhost si entra nell'app senza un vero login), ma
          // res.json() fallisce perché il corpo è HTML, non JSON. Qui non
          // c'è nessun utente reale da controllare, quindi in locale
          // consideriamo admin di default per poter vedere/testare
          // Analytics. In produzione (build con `npm run build`) questo
          // branch non forza mai isAdmin: import.meta.env.DEV è sempre
          // false nel bundle buildato, quindi lì conta solo il vero
          // /auth/me con l'email reale.
          if (import.meta.env.DEV) {
            console.warn(
              '[auth] /auth/me non ha risposto con JSON valido (normale in dev locale, vedi commento nel codice): isAdmin forzato a true solo per lo sviluppo.'
            )
            setIsAdmin(true)
          }
        }
      })
      .catch(() => setIsAuthenticated(false))
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return <div style={s.loader}>Caricamento...</div>
  }

  if (!isAuthenticated) {
    return <Landing onLogin={() => setIsAuthenticated(true)} />
  }

  return (
    <BrowserRouter>
      <div style={{ ...s.appLayout, flexDirection: isMobile ? 'column' : 'row' }}>
        <Sidebar isAdmin={isAdmin} />
        <main style={s.main}>
          <Suspense fallback={<div style={s.loader}>Caricamento...</div>}>
            <Routes>
              <Route path="/storico" element={<Dashboard />} />
              <Route path="/coda" element={<Coda />} />
              {/* Registrata solo se isAdmin: chi non è admin che prova ad
                  aprire /analytics direttamente finisce sul catch-all "*"
                  e viene rimandato a /storico. */}
              {isAdmin && <Route path="/analytics" element={<Analytics />} />}
              <Route path="*" element={<Navigate to="/storico" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </BrowserRouter>
  )
}

const s = {
  appLayout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    background: 'transparent',
  },
  main: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontSize: '1.1rem',
    color: '#666',
  },
}
